/* eslint-disable react/jsx-no-bind */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  Box,
  Button,
  H2,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  type MRT_PaginationState,
  TextInput,
  MRT_TableOptions,
  SampleRecording,
  ListDriveInfo,
  ColumnFilterPopover,
  FilterDropDown,
  BaseRecordingTableColumn,
  baseRecordingColumnMapping,
  Switch,
  MRT_Row,
  Icon,
  RowActionButton,
  ModalContainer,
  useDisclosure,
  SampleRecordings,
  EditRecording,
  RecordingStatus,
} from '@ouster/webviz-react-components';

import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMemo,
  useState,
  useLayoutEffect,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { MdClear, MdRefresh, MdSearch } from 'react-icons/md';
import { TbEyeStar } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecordingSearch } from 'src/hooks/useRecordingSearch';
import { hasUserSelectionOnTable } from 'src/utils/tableUtils';

import RecordingAccessConfig from '../../components/recordingTable/RecordingAccessConfig';
import RecordingDownloadPopover from '../../components/recordingTable/RecordingDownloadPopover';
import useFilters from '../../components/recordingTable/useFilters';
import {
  useFullyPublicListQuery,
  usePrivatePublicListQuery,
} from '../../components/recordingTable/useRecordingListQuery';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { RecordingPublicityAccessConfig } from '../../types';
import { DRIVES_PER_PAGE, FILTER_GROUPS } from '../../utils/constants';
import { convertFilterArrayToObject } from '../../utils/driveUtil';
import { fetchWithAuthV2 } from '../../utils/requests';

const saveSearchToLocalStorage = (search: string) => {
  const searchKey = `public_search`;
  localStorage.setItem(searchKey, search);
};

type RowActionsProps = {
  row: MRT_Row<ListDriveInfo>;
  openPrivateVizPage: (value: ListDriveInfo) => void;
  copyCloudPublicLink: (value: ListDriveInfo) => void;
  configRecordingPublicity: (value: ListDriveInfo) => void;
  updateRecordingInfo: (value: ListDriveInfo) => void;
  openRecordingDownloadPopover: (
    row: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const RowActions = ({
  row,
  openPrivateVizPage,
  copyCloudPublicLink,
  configRecordingPublicity,
  updateRecordingInfo,
  openRecordingDownloadPopover,
}: RowActionsProps) => {
  const { curUserOrg, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );
  return (
    <Box
      flexWrap="nowrap"
      display="flex"
      data-testid="public-collection-row-action-buttons"
    >
      {/* 
        only Ouster admin can Configure Sample Access
      */}
      {isOusterUser && curUserOrg?.role === 'ADMIN' && (
        <>
          <RowActionButton
            title="Edit File Name"
            icon={<Icon icon="edit" />}
            row={row.original}
            onClickHandler={updateRecordingInfo}
          />
          <RowActionButton
            title="Configure Sample Recording"
            icon={<TbEyeStar />}
            row={row.original}
            onClickHandler={configRecordingPublicity}
          />
        </>
      )}
      <RowActionButton<ListDriveInfo>
        title="Copy public link"
        icon={<Icon icon="share" />}
        row={row.original}
        onClickHandler={copyCloudPublicLink}
      />
      <RowActionButton<ListDriveInfo>
        title="Download recording"
        icon={<Icon icon="download" />}
        row={row.original}
        onClickHandler={openRecordingDownloadPopover}
      />

      {isOusterUser && (
        <RowActionButton<ListDriveInfo>
          title="Open private link"
          icon={<Icon icon="openInNew" />}
          row={row.original}
          onClickHandler={openPrivateVizPage}
        />
      )}
    </Box>
  );
};

/**
 * Drive public page component
 * @returns Drive public page
 */
export default function PrivatePublicCollection() {
  const {
    setSourceFileInfo,
    setOutputFileInfo,
    updateNewContentStatus,
    updateRecordingTitle,
  } = useStoreActions((actions) => actions.drive);

  const { isOusterUser, appUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );

  // get filterable options of sample data
  const { loadPrivatePublicFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { privatePublicFilterOptions } = useStoreState((storeState) => ({
    ...storeState.driveList,
  }));
  const filterOptions = convertFilterArrayToObject(privatePublicFilterOptions);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: DRIVES_PER_PAGE,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { filters, orderBy, sortOrder, setGroupFilters } = useFilters();
  const { showToast } = useChakraHook();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const { data, meta, isError, isRefetching, isLoading, isSuccess, refetch } =
    usePrivatePublicListQuery(
      curUserOrg,
      orderBy,
      sortOrder,
      pagination.pageIndex,
      pagination.pageSize,
      location.search
    );

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const filterFields = useMemo(
    () => [
      baseRecordingColumnMapping[BaseRecordingTableColumn.DUR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_TYPE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_GENERATION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.SN],
      baseRecordingColumnMapping[BaseRecordingTableColumn.MODE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDING_TITLE],
    ],
    []
  );
  const { searchTerm, setSearchTerm, filteredData } = useRecordingSearch(
    data,
    filterFields
  );

  const customTableOptions: Partial<MRT_TableOptions<ListDriveInfo>> = {
    onPaginationChange: setPagination,
    enablePagination: true,
    enableSortingRemoval: false,
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        openPrivateVizPage={openPrivateVizPage}
        copyCloudPublicLink={copyCloudPublicLink}
        openRecordingDownloadPopover={openRecordingDownloadPopover}
        configRecordingPublicity={configRecordingPublicity}
        updateRecordingInfo={updateRecordingInfo}
      />
    ),
    rowCount: meta?.totalCount ?? 0,
    state: {
      isLoading,
      pagination,
      showLoadingOverlay: false,
      showAlertBanner: isError,
      showProgressBars: isLoading || isRefetching,
      showSkeletons: isLoading || isRefetching,
    },
  };

  const [isFeaturedVisible, setIsFeaturedVisible] = useState(true);

  const { data: sampleRecordings, refetch: refetchFullyPublicList } =
    useFullyPublicListQuery(
      undefined, // curUserOrg - not needed for public queries
      'updated_at', // orderBy
      'desc', // sortOrder
      0, // pageIndex
      100, // pageSize
      '' // searchParams (no filters)
    );

  const featuredSampleData = useMemo(
    () =>
      sampleRecordings?.filter(
        (recording) =>
          recording.publication_access_level === 'PUBLIC_SAMPLE_FEATURED'
      ) || [],
    [sampleRecordings]
  );

  const {
    isOpen: isRecordingInfoModalOpen,
    onOpen: onRecordingInfoModalOpen,
    onClose: onRecordingInfoModalClose,
  } = useDisclosure();
  const recordingInfoRef = useRef<ListDriveInfo | undefined>(undefined);

  const [isUpdatingRecording, setIsUpdatingRecording] = useState(false);

  function updateRecordingInfo(recording: ListDriveInfo) {
    recordingInfoRef.current = recording;
    onRecordingInfoModalOpen();
  }
  async function submitRecordingInfoChange(recording: ListDriveInfo) {
    try {
      if (!curUserOrg?.id || !recording.recording_title) return;
      setIsUpdatingRecording(true);
      await updateRecordingTitle({
        orgId: curUserOrg.id,
        driveId: recording.id as number,
        title: recording.recording_title,
      });
      // Instead of refetching the whole list, we update the specific item in the cache
      const queryKey = [
        'private-public-drive-list',
        curUserOrg.id,
        orderBy,
        sortOrder,
        pagination.pageIndex,
        pagination.pageSize,
        location.search,
      ];

      queryClient.setQueryData<{ data: ListDriveInfo[] }>(
        queryKey,
        (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            data: oldData.data.map((item: ListDriveInfo) =>
              item.id === recording.id
                ? { ...item, recording_title: recording.recording_title }
                : item
            ),
          };
        }
      );

      // when the name is changed in here, we want to invalidate the drive-list query, so the drive query will be refetch
      // when user visit the private recording page
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'drive-list',
      });

      showToast({
        title: '',
        description: `Recording title has been updated`,
        status: 'success',
      });

      onRecordingInfoModalClose();
      recordingInfoRef.current = undefined;
    } catch (error) {
      showToast({
        title: 'Failed to update recording',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsUpdatingRecording(false);
    }
  }

  /* 
      Control for recording publicity configuration modal
    */
  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();
  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);
  const onPublicitySettingCloseHandler = useCallback(() => {
    publicityRecordingRef.current = undefined;
    if (localStorage.getItem('recordingAccessConfigChanged')) {
      // invalidate the drive list query to make sure the updated publicity setting is fetched
      // when user visit the private recording page
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'drive-list' ||
          query.queryKey[0] === 'shared-from-list',
      });
      refetch();
      refetchFullyPublicList();

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient, refetch, refetchFullyPublicList]);

  function configRecordingPublicity(recording: ListDriveInfo) {
    publicityRecordingRef.current = {
      organization_id: recording.organization_id,
      id: Number(recording.id),
      publication_uuid: recording.publication_uuid,
      data_category: recording.data_category,
      sensor_info: recording.sensor_info,
      start_time: recording.start_time,
      recording_title: recording.recording_title,
    };
    onPublicitySettingOpen();
  }

  function copyCloudPublicLink(recording: ListDriveInfo) {
    if (recording.public_url) {
      copyToClipboard(recording.public_url);
    }
  }

  function addMarketingTracking(recording: ListDriveInfo) {
    if (!appUser || !curUserOrg) return;
    if (!recording.sensor_info || recording.sensor_info.length === 0) return;
    const model = recording.sensor_info[0].sdk_prod_type;
    if (!model) return;

    const localDateStamp = new Date().toISOString();

    const trackingObj = {
      action: 'createOrUpdate',
      lookupField: 'email',
      programName: model,
      input: [
        {
          email: appUser?.email,
          sensorModel: model,
          sessionDuration: localDateStamp,
        },
      ],
    };

    try {
      const path = `/api/v1/organizations/${curUserOrg.id}/analytics/post-lead`;

      fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to send tracking info',
        body: JSON.stringify(trackingObj),
      });
    } catch (error) {
      console.error('Error sending marketing tracking data:', error);
    }
  }

  function openVizPage(recording: ListDriveInfo) {
    if (isLoading) return;
    if (hasUserSelectionOnTable()) return;
    if (recording.public_url) {
      addMarketingTracking(recording);
      updateNewContentStatus({
        organizationId: recording.organization_id,
        viewType: 'PUBLIC_PRIVATE',
      });
      const url = recording.public_url;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  function openPrivateVizPage(recording: ListDriveInfo) {
    if (isLoading) return;
    window.open(
      `/organization/1/drive/${recording.id}`,
      '_blank',
      'noopener,noreferrer'
    );
  }

  /* 
    Click on download button will triggered a popover with all downloadable links
  */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadRecordingId, setDownloadRecordingId] = useState('');
  const downloadRecordingTitleRef = useRef<string | undefined>(undefined);
  const downloadRecordingIsSlamProcessingRef = useRef(false);

  function openRecordingDownloadPopover(
    recording: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) {
    if (!event || !recording.publication_uuid) return;
    setAnchorEl(event.currentTarget);
    setDownloadRecordingId(recording.publication_uuid);
    downloadRecordingTitleRef.current = recording.recording_title ?? undefined;
    downloadRecordingIsSlamProcessingRef.current =
      recording.slam_generation_status === RecordingStatus.Running;
  }

  function closeDownloadMenu() {
    setOutputFileInfo([]);
    setSourceFileInfo([]);
    setAnchorEl(null);
  }

  function clearAllFilters() {
    localStorage.removeItem(`public_search`);
    navigate({
      pathname: location.pathname,
      search: '', // Clear search parameters
    });
  }

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, filterValues) => total + filterValues.length,
    0
  );

  /* 
    When we visit this page via nav menu, there is no search query
    in this case, we won't want to update our local storage with the empty query
    we will add the default query to the search and make decision after
    about whether we need to update query in local storage
  */
  const [isDefaultQueryReady, setIsDefaultQueryReady] = useState(false);
  useLayoutEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    if (location.search === '') {
      navigate(
        { search: 'order_by=updated_at&sort_order=desc' },
        { replace: true }
      );
    } else {
      setIsDefaultQueryReady(true);
    }
  }, [location.search, navigate]);

  /* 
    we will update local storage when
    - data is successfully fetched (live or cached)
    - and default query is setup so no accidentally wipe useful saved query
    - it doesn't happen during switch organization
  */
  useEffect(() => {
    if (isDefaultQueryReady && isSuccess) {
      saveSearchToLocalStorage(location.search);
    }
  }, [isDefaultQueryReady, isSuccess, location.search]);

  useEffect(() => {
    /* 
      We only use the filter from storage when it is not default filter
    */
    if (location.search !== '?order_by=updated_at&sort_order=desc') return;
    const searchKey = `public_search`;
    const storedSearch = localStorage.getItem(searchKey);

    if (storedSearch) {
      // Parse the stored search and ensure order_by is updated_at here.
      const params = new URLSearchParams(storedSearch);
      params.set('order_by', 'updated_at');
      const updatedSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: updatedSearch,
        },
        { replace: true }
      );
    }
  }, [navigate, location.pathname, location.search]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadPrivatePublicFilterOptions(curUserOrg.id);
    }
  }, [curUserOrg?.id, loadPrivatePublicFilterOptions]);

  return (
    <>
      <Box
        p="25px 36px 20px"
        h="100%"
        overflow="hidden"
        position="relative"
        display="grid"
        gridTemplateRows="auto auto 1fr auto"
        gap="2rem"
      >
        {/* Featured cards */}
        <Box display="grid" rowGap={4} data-testid="public-collection-featured">
          <Box
            display="flex"
            placeContent="space-between"
            alignItems="center"
            data-testid="public-collection-titlebar"
          >
            <H2 fontWeight="medium" fontSize={30}>
              Public collection
            </H2>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              fontWeight="medium"
              fontSize={16}
              mr={2}
            >
              <span>Show featured:</span>
              <Switch
                size="md"
                isChecked={isFeaturedVisible}
                onChange={() => setIsFeaturedVisible(!isFeaturedVisible)}
                data-testid="public-collection-featured-toggle"
              />
            </Box>
          </Box>

          {isFeaturedVisible && (
            <SampleRecordings
              featureRecordingDownloadHandler={openRecordingDownloadPopover}
              sampleData={featuredSampleData}
              copyCloudPublicLink={copyCloudPublicLink}
              openVizPage={openVizPage}
              isOusterAdmin={isOusterUser && curUserOrg?.role === 'ADMIN'}
              configRecordingPublicity={configRecordingPublicity}
            />
          )}
        </Box>

        {/* action console - search, filter selection and refresh */}
        <Box display="grid" rowGap="1rem">
          <Box
            display="grid"
            gridTemplateColumns="1fr auto"
            gap={2}
            alignItems="start"
            data-testid="public-collection-filters-bar"
          >
            {/* filters and search controls */}
            <Box
              display="flex"
              gap={2}
              alignItems="center"
              flexWrap="wrap"
              data-testid="public-collection-filters"
            >
              <InputGroup
                maxW="25ch"
                data-testid="public-collection-search-group"
              >
                <InputLeftElement pointerEvents="none">
                  <MdSearch size={18} />
                </InputLeftElement>
                <TextInput
                  pl="2.5rem"
                  pr="3.5rem"
                  placeholder="Search ..."
                  type="search"
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  data-testid="public-collection-search-input"
                />
                <InputRightElement>
                  <IconButton
                    onClick={() => setSearchTerm('')}
                    data-testid="public-collection-search-clear"
                  >
                    <MdClear size={16} />
                  </IconButton>
                </InputRightElement>
              </InputGroup>

              {FILTER_GROUPS.map((groupedFilter) => (
                <FilterDropDown
                  title={groupedFilter.title}
                  filterKey={groupedFilter.filterKeys}
                  filterOptions={filterOptions}
                  setGroupFilters={setGroupFilters}
                  selectedFilters={filters}
                  key={groupedFilter.title}
                  data-testid={`public-collection-filter-${groupedFilter.title
                    .toLowerCase()
                    .replace(/\s/g, '-')}`}
                />
              ))}

              {totalSelectedFilters > 0 && (
                <Button
                  variant="ghost"
                  colorScheme="gray"
                  gap={1}
                  onClick={() => clearAllFilters()}
                  fontSize="12px"
                  data-testid="public-collection-clear-filters"
                >
                  <MdClear size={18} /> Clear Filters
                </Button>
              )}
            </Box>
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={() => refetch()}
              data-testid="public-collection-refresh"
            >
              <MdRefresh size={18} /> Refresh List
            </Button>
          </Box>
        </Box>

        {/* table */}
        <SampleRecording
          filters={filters || {}}
          maxHeight="100%"
          filteredData={filteredData}
          isOusterUser={isOusterUser}
          setFilterAnchorEl={setFilterAnchorEl}
          setSelectedColKeys={setSelectedColKeys}
          openVizPage={openVizPage}
          openPrivateVizPage={openPrivateVizPage}
          copyCloudPublicLink={copyCloudPublicLink}
          openRecordingDownloadPopover={openRecordingDownloadPopover}
          customTableOptions={customTableOptions}
        />
      </Box>
      {anchorEl && (
        <RecordingDownloadPopover
          anchorEl={anchorEl}
          closeDownloadMenu={() => closeDownloadMenu()}
          downloadRecordingId={downloadRecordingId}
          orgId={1}
          isPublic
          recordingTitle={downloadRecordingTitleRef.current}
          isSlamProcessing={downloadRecordingIsSlamProcessingRef.current}
          data-testid="public-collection-download-popover"
        />
      )}
      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={filters}
          setGroupFilters={setGroupFilters}
          data-testid="public-collection-column-filter-popover"
        />
      )}
      {recordingInfoRef.current && (
        <EditRecording
          recording={recordingInfoRef.current}
          onClose={onRecordingInfoModalClose}
          onSubmit={submitRecordingInfoChange}
          isLoading={isUpdatingRecording}
          isRecordingInfoModalOpen={isRecordingInfoModalOpen}
          heading="Edit Recording"
          data-testid="public-collection-edit-recording-modal"
        />
      )}

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
          data-testid="public-collection-access-config-modal"
        />
      )}
    </>
  );
}
